/**
 * BluePrint — Graphite Violet Design System Tokens
 * 
 * Canonical design token definitions for the BluePrint platform.
 * Used by:
 *   - UX Agent (Phase 2.5) as reference input for design token generation
 *   - Component catalogue as default theme
 *   - Development Agent as styling reference in implementation prompts
 * 
 * Token structure follows the Design Tokens Community Group (DTCG) format
 * adapted for TypeScript consumption.
 * 
 * @version 1.0
 * @since Sprint 11
 */

// ─── COLOR PALETTE ──────────────────────────────────────────────────────────

export const colors = {
  // Primary
  primary: {
    50:  '#F5F3FF',
    100: '#EDE9FE',
    200: '#DDD6FE',
    300: '#C4B5FD',
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',  // ← Brand primary — Graphite Violet
    700: '#6D28D9',
    800: '#5B21B6',
    900: '#4C1D95',
    950: '#2E1065',
    DEFAULT: '#7C3AED',
  },

  // Accent — Pink
  accent: {
    50:  '#FDF2F8',
    100: '#FCE7F3',
    200: '#FBCFE8',
    300: '#F9A8D4',
    400: '#F472B6',
    500: '#EC4899',  // ← Brand accent — Pink
    600: '#DB2777',
    700: '#BE185D',
    800: '#9D174D',
    900: '#831843',
    DEFAULT: '#EC4899',
  },

  // Signal — Green (success, positive indicators)
  signal: {
    50:  '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#00E5A0',  // ← Brand signal — Signal Green
    600: '#059669',
    700: '#047857',
    800: '#065F46',
    900: '#064E3B',
    DEFAULT: '#00E5A0',
  },

  // Graphite — Dark backgrounds
  graphite: {
    50:  '#F8F8FC',
    100: '#F1F0F7',
    200: '#E4E3EE',
    300: '#C8C6D7',
    400: '#9795AA',
    500: '#6B687E',
    600: '#4A4860',
    700: '#2D2B3D',
    800: '#1A1828',
    900: '#12101E',
    950: '#0D0D14',  // ← Brand background — Dark Graphite
    DEFAULT: '#0D0D14',
  },

  // Semantic colors
  semantic: {
    success:  '#00E5A0',
    warning:  '#F59E0B',
    error:    '#EF4444',
    info:     '#3B82F6',
    muted:    '#6B7280',
  },

  // Surface colors (for cards, panels, modals)
  surface: {
    background:     '#0D0D14',
    card:           '#1A1828',
    cardHover:      '#2D2B3D',
    panel:          '#12101E',
    overlay:        'rgba(13, 13, 20, 0.8)',
    border:         '#2D2B3D',
    borderSubtle:   '#1A1828',
  },

  // Text colors
  text: {
    primary:    '#FFFFFF',
    secondary:  '#C8C6D7',
    tertiary:   '#9795AA',
    muted:      '#6B687E',
    inverse:    '#0D0D14',
    link:       '#7C3AED',
    linkHover:  '#A78BFA',
  },
} as const;

// ─── TYPOGRAPHY ─────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: {
    display:  "'Manrope', system-ui, -apple-system, sans-serif",
    body:     "'Manrope', system-ui, -apple-system, sans-serif",
    mono:     "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  },

  fontSize: {
    xs:    '0.75rem',     // 12px
    sm:    '0.875rem',    // 14px
    base:  '1rem',        // 16px
    lg:    '1.125rem',    // 18px
    xl:    '1.25rem',     // 20px
    '2xl': '1.5rem',      // 24px
    '3xl': '1.875rem',    // 30px
    '4xl': '2.25rem',     // 36px
    '5xl': '3rem',        // 48px
  },

  fontWeight: {
    normal:    '400',
    medium:    '500',
    semibold:  '600',
    bold:      '700',
    extrabold: '800',
  },

  lineHeight: {
    tight:   '1.25',
    snug:    '1.375',
    normal:  '1.5',
    relaxed: '1.625',
    loose:   '2',
  },

  letterSpacing: {
    tighter: '-0.05em',
    tight:   '-0.025em',
    normal:  '0',
    wide:    '0.025em',
    wider:   '0.05em',
  },
} as const;

// ─── SPACING ────────────────────────────────────────────────────────────────

export const spacing = {
  px:  '1px',
  0:   '0',
  0.5: '0.125rem',   // 2px
  1:   '0.25rem',    // 4px
  1.5: '0.375rem',   // 6px
  2:   '0.5rem',     // 8px
  2.5: '0.625rem',   // 10px
  3:   '0.75rem',    // 12px
  4:   '1rem',       // 16px
  5:   '1.25rem',    // 20px
  6:   '1.5rem',     // 24px
  8:   '2rem',       // 32px
  10:  '2.5rem',     // 40px
  12:  '3rem',       // 48px
  16:  '4rem',       // 64px
  20:  '5rem',       // 80px
  24:  '6rem',       // 96px
} as const;

// ─── BORDER RADIUS ──────────────────────────────────────────────────────────

export const borderRadius = {
  none:  '0',
  sm:    '0.25rem',    // 4px
  base:  '0.5rem',     // 8px  — default for cards, inputs
  md:    '0.75rem',    // 12px — buttons, badges
  lg:    '1rem',       // 16px — modals, panels
  xl:    '1.5rem',     // 24px — large cards
  full:  '9999px',     // pills, avatars
} as const;

// ─── SHADOWS ────────────────────────────────────────────────────────────────

export const shadows = {
  none:   'none',
  sm:     '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  base:   '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px -1px rgba(0, 0, 0, 0.4)',
  md:     '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -2px rgba(0, 0, 0, 0.4)',
  lg:     '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -4px rgba(0, 0, 0, 0.4)',
  xl:     '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.4)',
  glow:   '0 0 20px rgba(124, 58, 237, 0.3)',    // Violet glow for focus/active states
  inner:  'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
} as const;

// ─── BREAKPOINTS ────────────────────────────────────────────────────────────
// Sprint 11 UX.C2 — re-export the canonical map from
// `packages/shared/src/breakpoints.ts` so the Web tokens and the API's
// responsiveSpecGenerator share a single source of truth (issue #576 AC:
// "Breakpoint constants sourced from design tokens (no hardcoded pixel
// values)"). Editing the values now requires touching only the shared file.

import { RESPONSIVE_BREAKPOINTS } from '@blueprint/shared';
export const breakpoints = RESPONSIVE_BREAKPOINTS;

// ─── TRANSITIONS ────────────────────────────────────────────────────────────

export const transitions = {
  duration: {
    fast:    '150ms',
    base:    '200ms',
    slow:    '300ms',
    slower:  '500ms',
  },
  easing: {
    default:  'cubic-bezier(0.4, 0, 0.2, 1)',
    in:       'cubic-bezier(0.4, 0, 1, 1)',
    out:      'cubic-bezier(0, 0, 0.2, 1)',
    inOut:    'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce:   'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
} as const;

// ─── Z-INDEX SCALE ──────────────────────────────────────────────────────────

export const zIndex = {
  hide:      -1,
  base:      0,
  dropdown:  1000,
  sticky:    1100,
  banner:    1200,
  overlay:   1300,
  modal:     1400,
  popover:   1500,
  toast:     1600,
  tooltip:   1700,
} as const;

// ─── COMPONENT-SPECIFIC TOKENS ──────────────────────────────────────────────

export const components = {
  button: {
    primary: {
      bg:         colors.primary.DEFAULT,
      bgHover:    colors.primary[700],
      bgActive:   colors.primary[800],
      text:       '#FFFFFF',
      border:     'none',
      radius:     borderRadius.md,
      shadow:     shadows.sm,
      shadowHover: shadows.glow,
    },
    secondary: {
      bg:         'transparent',
      bgHover:    colors.primary[50],
      bgActive:   colors.primary[100],
      text:       colors.primary.DEFAULT,
      border:     `1px solid ${colors.primary.DEFAULT}`,
      radius:     borderRadius.md,
      shadow:     'none',
      shadowHover: shadows.sm,
    },
    ghost: {
      bg:         'transparent',
      bgHover:    colors.graphite[800],
      bgActive:   colors.graphite[700],
      text:       colors.text.secondary,
      border:     'none',
      radius:     borderRadius.md,
      shadow:     'none',
      shadowHover: 'none',
    },
  },

  input: {
    bg:           colors.graphite[800],
    bgFocus:      colors.graphite[700],
    text:         colors.text.primary,
    placeholder:  colors.text.muted,
    border:       colors.surface.border,
    borderFocus:  colors.primary.DEFAULT,
    radius:       borderRadius.base,
    shadow:       shadows.inner,
    shadowFocus:  shadows.glow,
  },

  card: {
    bg:           colors.surface.card,
    bgHover:      colors.surface.cardHover,
    border:       colors.surface.border,
    radius:       borderRadius.lg,
    shadow:       shadows.base,
    shadowHover:  shadows.md,
    padding:      spacing[6],
  },

  badge: {
    success: { bg: '#D1FAE5', text: '#065F46' },
    warning: { bg: '#FEF3C7', text: '#92400E' },
    error:   { bg: '#FEE2E2', text: '#991B1B' },
    info:    { bg: '#DBEAFE', text: '#1E40AF' },
    neutral: { bg: '#F3F4F6', text: '#374151' },
    violet:  { bg: colors.primary[100], text: colors.primary[800] },
  },

  modal: {
    bg:        colors.surface.card,
    overlay:   colors.surface.overlay,
    radius:    borderRadius.xl,
    shadow:    shadows.xl,
    padding:   spacing[8],
    maxWidth:  '640px',
  },

  sidebar: {
    bg:        colors.graphite[900],
    width:     '240px',
    borderRight: `1px solid ${colors.surface.border}`,
  },

  chat: {
    agentBubble: {
      bg:     colors.surface.card,
      text:   colors.text.primary,
      radius: borderRadius.lg,
    },
    userBubble: {
      bg:     colors.primary.DEFAULT,
      text:   '#FFFFFF',
      radius: borderRadius.lg,
    },
    quickReply: {
      bg:        'transparent',
      bgHover:   colors.graphite[800],
      border:    colors.surface.border,
      text:      colors.text.secondary,
      radius:    borderRadius.base,
    },
  },
} as const;

// ─── COMPLETE TOKEN SET EXPORT ──────────────────────────────────────────────

export const designTokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  breakpoints,
  transitions,
  zIndex,
  components,
} as const;

export type DesignTokens = typeof designTokens;
export default designTokens;
